import { useCallback, useRef, useState } from "react";

/**
 * Loads rows for one dashboard chart via the connected `sendPostRequest` action.
 * `loading` is only true until the first response; later reloads set `refreshing`
 * so the chart stays visible. Responses from superseded requests are ignored.
 * `params` is the payload that produced the current `rows`.
 */
const useChartRequest = (sendPostRequest, url) => {
  const [rows, setRows] = useState([]);
  const [params, setParams] = useState({});
  const [status, setStatus] = useState("loading");
  const [refreshing, setRefreshing] = useState(false);
  const latest = useRef(0);

  const load = useCallback(
    (payload = {}) => {
      const id = ++latest.current;
      setRefreshing(true);
      sendPostRequest({
        url,
        data: payload,
        onSuccess: (response) => {
          if (id !== latest.current) return;
          const payload = response && response.data;
          const data = Array.isArray(payload)
            ? payload
            : payload && Array.isArray(payload.data)
              ? payload.data
              : [];
          setRows(data);
          setParams(payload);
          setStatus("ready");
          setRefreshing(false);
        },
        onError: () => {
          if (id !== latest.current) return;
          setRows([]);
          setStatus("error");
          setRefreshing(false);
        },
      });
    },
    [sendPostRequest, url]
  );

  return {
    rows,
    params,
    loading: status === "loading",
    error: status === "error",
    refreshing: refreshing && status !== "loading",
    load,
  };
};

export default useChartRequest;
